<template>
  <div class="flex min-h-screen flex-col bg-night">
    <AppHeader />

    <main class="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pb-10 pt-6 sm:px-6 sm:pt-10">
      <section class="flex items-center gap-4">
        <img src="../assets/logo.webp" alt="" class="animate-float h-16 w-16 flex-none rounded-full object-cover ring-4 ring-hippo/60 sm:h-20 sm:w-20" />
        <div class="flex flex-col gap-1.5">
          <span class="text-[13px] font-semibold uppercase tracking-[0.08em] text-beer">Grundgesetz</span>
          <h1 class="font-display text-[40px] font-extrabold leading-none tracking-tight sm:text-5xl">Unsere Regeln</h1>
        </div>
      </section>
      <p class="-mt-2 text-[15px] leading-relaxed text-slate-400">Wer sie bricht, zahlt. So einfach ist das.</p>

      <div v-if="loading" class="flex h-48 items-center justify-center">
        <div class="h-10 w-10 animate-spin rounded-full border-2 border-edge border-t-beer" aria-label="Lädt"></div>
      </div>

      <div v-else-if="error" class="flex flex-col items-center gap-4 py-12 text-center">
        <p class="text-red-300">{{ error }}</p>
        <button @click="loadRules" class="min-h-[44px] rounded-full bg-beer px-5 font-semibold text-night">Erneut versuchen</button>
      </div>

      <template v-else>
        <div role="tablist" aria-label="Regelart" class="grid grid-cols-2 gap-1 rounded-2xl border border-edge bg-panel p-1">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            role="tab"
            :aria-selected="activeTab === tab.id"
            @click="activeTab = tab.id"
            class="min-h-[44px] rounded-xl text-[15px] font-semibold transition-colors"
            :class="activeTab === tab.id ? 'bg-beer text-night' : 'text-slate-300 hover:text-white'"
          >
            {{ tab.label }}
          </button>
        </div>

        <transition-group tag="ol" name="list" appear class="flex flex-col gap-2.5" :key="activeTab">
          <li
            v-for="(rule, index) in activeRules"
            :key="rule.id"
            class="flex items-start gap-3.5 rounded-2xl border border-edge bg-panel p-3.5"
            :style="{ transitionDelay: `${index * 40}ms` }"
          >
            <span class="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] bg-edge font-display text-base font-extrabold text-beer">{{ index + 1 }}</span>
            <span class="pt-1.5 text-[15px] leading-relaxed">{{ rule.ruleDescription }}</span>
          </li>
        </transition-group>
        <p v-if="activeRules.length === 0" class="py-4 text-center text-slate-400">Keine Regeln verfügbar</p>

        <section v-if="catalog.length" v-reveal class="flex flex-col gap-3 pt-2">
          <h2 class="font-display text-[22px] font-extrabold sm:text-[26px]">Strafenkatalog</h2>
          <ul class="overflow-hidden rounded-[18px] border border-edge bg-panel">
            <li v-for="item in catalog" :key="item.id" class="flex items-center justify-between border-b border-edge px-4 py-3.5 last:border-b-0">
              <span class="text-[15px]">{{ item.type }}</span>
              <span class="text-[15px] font-semibold tabular-nums text-beer">{{ formatEuro(item.value) }}</span>
            </li>
          </ul>
        </section>
      </template>
    </main>

    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import RuleService, { fetchPunishmentTypes, PunishmentType } from '@/services/RuleService';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import Rules from '@/types/Rules';
import { formatEuro } from '@/utils/format';

const tabs = [
  { id: 'public', label: 'Allgemein' },
  { id: 'game', label: 'Spielregeln' },
] as const;

const activeTab = ref<'public' | 'game'>('public');
const rules = ref<Rules>({
  publicRules: [],
  gameRules: [],
});
const catalog = ref<PunishmentType[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

const activeRules = computed(() => (activeTab.value === 'public' ? rules.value.publicRules : rules.value.gameRules));

const loadRules = async (): Promise<void> => {
  try {
    loading.value = true;
    error.value = null;
    [rules.value, catalog.value] = await Promise.all([RuleService.fetchRules(), fetchPunishmentTypes()]);
  } catch (err) {
    error.value = 'Fehler beim Laden der Regeln';
    console.error('Error loading rules:', err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadRules();
});
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .list-enter-active {
    transition: opacity 0.35s ease-out, transform 0.35s ease-out;
  }

  .list-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }
}
</style>
